import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, CircularProgress, Typography} from '@mui/material';

export const BROCHURE_PDF_SRC = `${process.env.PUBLIC_URL}/assets/dash-brochure.pdf`;

async function loadPdfJs() {
    const pdfjs = await import('pdfjs-dist/build/pdf');
    const worker = await import('pdfjs-dist/build/pdf.worker.entry');
    pdfjs.GlobalWorkerOptions.workerSrc = worker;
    return pdfjs;
}

/**
 * Renders the brochure PDF page to a canvas at full container width — borderless, sharp on retina.
 */
const BrochurePdf = () => {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const renderTaskRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const renderPage = useCallback(async () => {
        const container = containerRef.current;
        const canvas = canvasRef.current;
        if (!container || !canvas) return;

        const width = container.clientWidth || container.getBoundingClientRect().width || window.innerWidth;
        if (width <= 0) return;

        try {
            setError(null);
            setLoading(true);

            if (renderTaskRef.current) {
                renderTaskRef.current.cancel();
            }

            const pdfjs = await loadPdfJs();
            const pdf = await pdfjs.getDocument(BROCHURE_PDF_SRC).promise;
            const page = await pdf.getPage(1);

            const baseViewport = page.getViewport({scale: 1});
            const scale = width / baseViewport.width;
            const viewport = page.getViewport({scale});
            const dpr = window.devicePixelRatio || 1;

            canvas.width = Math.floor(viewport.width * dpr);
            canvas.height = Math.floor(viewport.height * dpr);

            const context = canvas.getContext('2d');
            context.setTransform(dpr, 0, 0, dpr, 0, 0);
            context.clearRect(0, 0, viewport.width, viewport.height);

            const task = page.render({canvasContext: context, viewport});
            renderTaskRef.current = task;
            await task.promise;

            if (renderTaskRef.current === task) {
                renderTaskRef.current = null;
            }
        } catch (err) {
            if (err?.name === 'RenderingCancelledException') return;
            setError(err?.message || 'Failed to load brochure PDF');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        renderPage();

        const container = containerRef.current;
        if (!container || typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const observer = new ResizeObserver(() => {
            renderPage();
        });
        observer.observe(container);

        return () => {
            observer.disconnect();
            if (renderTaskRef.current) {
                renderTaskRef.current.cancel();
            }
        };
    }, [renderPage]);

    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                lineHeight: 0,
                position: 'relative',
                minHeight: loading ? 200 : undefined,
            }}
        >
            {loading && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'grid',
                        placeItems: 'center',
                    }}
                >
                    <CircularProgress size={32}/>
                </Box>
            )}
            {error && (
                <Typography color="error" variant="body2" sx={{p: 2}}>
                    {error}
                </Typography>
            )}
            <canvas
                ref={canvasRef}
                aria-label="DASH brochure — intelligence platform overview from collection to publication"
                style={{
                    display: error ? 'none' : 'block',
                    width: '100%',
                    height: 'auto',
                    margin: 0,
                    padding: 0,
                    border: 0,
                    verticalAlign: 'top',
                }}
            />
        </Box>
    );
};

export default BrochurePdf;
